import { Box, Text, Flex, Image } from '@chakra-ui/react';
import { useIsAuthenticated } from 'react-auth-kit';
import { parseISO, isBefore, isAfter, parse } from 'date-fns';
import { Pelatihan } from '../../../../../entities/Pelatihan';
import { usePelatihanSayaContext } from '../../../controller/registered/pelatihan_saya';
import { PiStarFill, PiUsers } from 'react-icons/pi';
import { useHistory } from 'react-router-dom';

/**
 * Component for displaying training cards.
 * @component
 * @param {Object} props - Component properties.
 * @param {Pelatihan} props.pelatihan - Training object to be displayed.
 * @returns {JSX.Element} - JSX for displaying the training card.
 * @author Bagas Prasetyadi
 */

const CardItemPelatihan = ({ pelatihan }: { pelatihan: Pelatihan }) => {
  const history = useHistory();

  // Call Controller
  const { currentTime } = usePelatihanSayaContext().state;

  const currentDate = currentTime;
  const location = window.location.pathname;

  // use is authenticated
  const isAuthenticated = useIsAuthenticated();

  const isTrainingCompleted = (trainingEndDate) => {
    // Convert the training completion date to a Date object
    const endDate = parseISO(trainingEndDate);

    // Get the current date
    const now = new Date();

    // Checks whether the training end date is past today's date
    return isBefore(endDate, now);
  };

  const parseDate = (dateString) => {
    return parse(dateString, 'yyyy-MM-dd HH:mm:ss', new Date());
  };

  const parsedWaktuMulai = parseDate(pelatihan.waktu_mulai);
  const parsedWaktuSelesai = parseDate(pelatihan.waktu_selesai);

  const handleNavigate = () => {
    window.scrollTo(0, 0);

    if (
      isAuthenticated() !== false &&
      pelatihan.status_pelatihan === 'Sedang Mengikuti'
    ) {
      if (
        isAfter(parseISO(currentDate), parsedWaktuMulai) &&
        isBefore(parseISO(currentDate), parsedWaktuSelesai)
      ) {
        history.push(`/detail_pelatihan_saya/${pelatihan.uuid}`);
      } else {
        history.push(`/detail/pelatihan/${pelatihan.uuid}`);
      }
    } else {
      history.push(`/detail/pelatihan/${pelatihan.uuid}`);
    }
  };

  return (
    <Box
      maxW={{ base: 'full', md: '470px' }}
      className="group"
      mr={location === '/' ? 8 : 0}
    >
      <Box
        bg="white"
        rounded="lg"
        overflow="hidden"
        border="1px solid"
        borderColor="gray.200"
        transition="all"
        transitionDuration="500ms"
        _hover={{ shadow: 'lg', cursor: 'pointer' }}
        p={4}
        onClick={handleNavigate}
      >
        {/* Image */}
        <Box w="full" position="relative" maxH="280px" mb="4">
          <Image
            src={pelatihan.path_thumbnail}
            backgroundSize="cover"
            style={{ aspectRatio: '2/1' }}
            rounded="lg"
            opacity={isTrainingCompleted(pelatihan.waktu_selesai) ? '0.5' : '1'}
          />
          <Text
            position="absolute"
            top={0}
            right={0}
            mt={4}
            mr={4}
            rounded="xl"
            py={1}
            px={4}
            fontSize="sm"
            bg="Cream"
          >
            Angkatan {pelatihan.angkatan}
          </Text>
          {isTrainingCompleted(pelatihan.waktu_selesai) && (
            <Box
              position="absolute"
              top="50%"
              left="50%"
              transform={`translate(-50%, -50%) rotate(-6deg)`}
              border="2px"
              borderColor="red.600"
              py={1}
              px={2}
              w="70%"
              textAlign="center"
            >
              <Text color="red.600" fontSize="3xl" fontWeight="semibold">
                {isTrainingCompleted(pelatihan.waktu_selesai) ? 'SELESAI' : ''}
              </Text>
            </Box>
          )}
        </Box>

        {/* Title and other things */}
        <Box>
          {/* Judul */}
          <Text fontSize="lg" fontWeight="semibold">
            {pelatihan.judul_pelatihan.length > 39
              ? pelatihan.judul_pelatihan.substring(0, 39) + '...'
              : pelatihan.judul_pelatihan}
          </Text>

          <Flex direction="row" alignItems="center" className="gap-2">
            {/* Rating */}
            <Flex alignItems="center" direction="row" className="gap-1">
              <Text fontSize="sm">{pelatihan.rating ?? 0}</Text>
              <PiStarFill size={13} className="text-yellow-500" />
            </Flex>
            <Text>|</Text>
            {/* Total Participant */}
            <Flex direction="row" alignItems="center" className="gap-1">
              <PiUsers size={13} />
              <Text>{pelatihan.count_peserta} Peserta</Text>
            </Flex>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

export default CardItemPelatihan;
